/**
 * AttributeStudioScreen — Attribute & Pricing Configuration Studio
 *
 * Tabs:
 *  1. Attribute Library  — global registry with search, industry coverage, add/delete
 *  2. Attribute Order    — drag-and-drop DisplayOrder editor (unchanged)
 *  3. Industry Config    — Matrix grid (industries × attributes), checkboxes + per-industry label edit
 *  4. Country Settings   — Globalization preview (number/currency/date formats per country)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Trash2, ArrowUp, ArrowDown, Library, Network, Globe2, FlaskConical,
  ArrowLeft, GripVertical, ListOrdered, Search, Settings2, Check, X, Pencil,
  Layers, Save, LayoutGrid, Eye,
} from 'lucide-react';
import ItemScreensTab from './ItemScreensTab';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken } from '../../lib/api';
import { AttributeFormPanel, GROUP_LEGEND, GetInfoPreviewModal } from './AttributeFormPanel';

const hdrs = () => ({ Authorization: `Bearer ${getToken() || ''}`, 'Content-Type': 'application/json' });

// ═══════════════════════════════════════════════════
// TIER 1 — Attribute Library (split: Classification | Pricing)
// ═══════════════════════════════════════════════════
const PRICING_GROUPS = new Set(['grp_pricing']);
const TAX_GROUPS = new Set(['grp_tax']);
const CLASSIFICATION_GROUPS = new Set(['grp_classification', 'grp_style', 'grp_physical', 'grp_compliance']);

// Per-group section style config for Library tab dynamic rendering
const GROUP_SECTION_STYLES = {
  grp_classification: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
  grp_style:          { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
  grp_physical:       { bg: '#fdf4ff', border: '#e9d5ff', text: '#7c3aed' },
  grp_compliance:     { bg: '#fefce8', border: '#fde68a', text: '#92400e' },
  grp_pricing:        { bg: '#fff7ed', border: '#fed7aa', text: '#b45309' },
  grp_tax:            { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
};
const getGroupStyle = (group) => {
  const s = GROUP_SECTION_STYLES[group.GroupId];
  if (s) return s;
  const c = group.Color || '#6b7280';
  return { bg: `${c}18`, border: `${c}40`, text: c };
};

// Country-aware tax label adapters (India→GST, UAE→VAT, etc.)
const TAX_LABEL_MAP = {
  GST:         { inputTaxName: 'GST Input (Purchase)', outputTaxName: 'GST Output (Sales)', cessValue: 'Cess %', hsnTaxRate: 'HSN Tax Rate', baseUnitName: 'Base Unit' },
  VAT:         { inputTaxName: 'VAT Input (Purchase)', outputTaxName: 'VAT Output (Sales)', cessValue: 'VAT Exempt %', hsnTaxRate: 'HS Code Rate', baseUnitName: 'Base Unit' },
  'Sales Tax': { inputTaxName: 'Sales Tax Input', outputTaxName: 'Sales Tax Output', cessValue: 'Surtax %', hsnTaxRate: 'HTS Rate', baseUnitName: 'Base Unit' },
};
const applyTaxLabel = (attr, taxSystem) => {
  if (!taxSystem || attr.GroupId !== 'grp_tax') return attr;
  const map = TAX_LABEL_MAP[taxSystem];
  if (!map || !map[attr.AttributeId]) return attr;
  return { ...attr, Label: map[attr.AttributeId] };
};

// ── Row context menu ─────────────────────────────────────────────────────────
function RowCtxMenu({ attr, groups, onClose, onEdit, onDelete, onMoveUp, onMoveDown, onPreview, canMoveUp, canMoveDown, anchorRef }) {
  const menuRef = useRef(null);
  // Position below the anchor button
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4 + window.scrollY, left: r.right - 160 + window.scrollX });
    }
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target) && e.target !== anchorRef?.current) onClose(); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [onClose, anchorRef]);

  const item = (label, icon, onClick, danger) => (
    <div onClick={() => { onClick(); onClose(); }}
      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', fontSize: 11, cursor: 'pointer', color: danger ? '#dc2626' : '#1e293b', whiteSpace: 'nowrap' }}
      onMouseEnter={e => e.currentTarget.style.background = danger ? '#fef2f2' : '#f1f5f9'}
      onMouseLeave={e => e.currentTarget.style.background = ''}>
      <span style={{ width: 14, display: 'flex', alignItems: 'center' }}>{icon}</span>{label}
    </div>
  );

  return (
    <div ref={menuRef} style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.15)', minWidth: 160, overflow: 'hidden' }}>
      {item('Edit', <Pencil size={12} />, onEdit)}
      {attr.ControlType === 'LookUpEdit' && attr.SourceTable && item('Preview GetInfo', <Eye size={12} />, onPreview)}
      <div style={{ borderTop: '1px solid #f1f5f9' }} />
      {item('Move Up', <ArrowUp size={12} />, onMoveUp, false)}
      {item('Move Down', <ArrowDown size={12} />, onMoveDown, false)}
      <div style={{ borderTop: '1px solid #f1f5f9' }} />
      {item('Delete', <Trash2 size={12} />, onDelete, true)}
    </div>
  );
}

const AttrTable = ({ attrs, groups, industryCounts, selectedAttr, onSelect, onDelete, pendingDelete, onPendingDelete, onGroupChange, onMoveUp, onMoveDown, onPreview, emptyMsg }) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const btnRefs = useRef({});

  return (
    <div className="erp-grid-wrapper" style={{ maxHeight: 240, overflow: 'auto' }}>
      <table className="erp-grid">
        <thead>
          <tr>
            <th>Label</th>
            <th>Attribute ID</th>
            <th>Control</th>
            <th>Group</th>
            <th style={{ textAlign: 'center' }} title="Industries using this attribute">Inds.</th>
            <th style={{ width: 48, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {attrs.map((a, idx) => (
            <tr key={a.AttributeId} data-testid={`attr-row-${a.AttributeId}`}
              onClick={() => onSelect(a)}
              style={{ cursor: 'pointer', background: selectedAttr?.AttributeId === a.AttributeId ? 'var(--erp-primary-light, #eff6ff)' : undefined }}>
              <td className="cell-bold">{a.Label}</td>
              <td className="cell-mono" style={{ fontSize: 10 }}>{a.AttributeId}</td>
              <td style={{ fontSize: 10 }}>{a.ControlType}</td>
              <td onClick={e => e.stopPropagation()} style={{ fontSize: 10, minWidth: 120 }}>
                <select value={a.GroupId || ''} data-testid={`group-select-${a.AttributeId}`}
                  onChange={e => onGroupChange && onGroupChange(a.AttributeId, e.target.value)}
                  style={{ fontSize: 10, border: '1px solid var(--erp-border)', borderRadius: 3, padding: '1px 4px', background: 'var(--erp-surface-alt)', cursor: 'pointer', width: '100%', maxWidth: 160 }}>
                  <option value="">— (none) —</option>
                  {groups.map(g => <option key={g.GroupId} value={g.GroupId}>{g.Label}</option>)}
                </select>
              </td>
              <td style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 10, background: (industryCounts[a.AttributeId] || 0) > 0 ? 'var(--erp-primary)' : 'var(--erp-surface-alt)', color: (industryCounts[a.AttributeId] || 0) > 0 ? '#fff' : 'var(--erp-text-muted)', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>
                  {industryCounts[a.AttributeId] || 0}
                </span>
              </td>
              <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center', position: 'relative' }}>
                {pendingDelete === a.AttributeId ? (
                  <span style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <button onClick={() => onDelete(a.AttributeId)}
                      style={{ fontSize: 9, color: '#dc2626', padding: '2px 6px', border: '1px solid #dc2626', borderRadius: 3, background: '#fff7f7', cursor: 'pointer', fontWeight: 700 }}>
                      Confirm
                    </button>
                    <button onClick={() => onPendingDelete(null)}
                      style={{ fontSize: 9, padding: '2px 5px', border: '1px solid var(--erp-border)', borderRadius: 3, background: '#fff', cursor: 'pointer' }}>
                      ×
                    </button>
                  </span>
                ) : (
                  <button
                    ref={el => btnRefs.current[a.AttributeId] = el}
                    data-testid={`attr-ctx-${a.AttributeId}`}
                    onClick={() => setOpenMenuId(id => id === a.AttributeId ? null : a.AttributeId)}
                    style={{ background: 'none', border: '1px solid transparent', borderRadius: 4, cursor: 'pointer', padding: '2px 5px', color: 'var(--erp-text-muted)', fontSize: 14, lineHeight: 1 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--erp-border)'; e.currentTarget.style.background = 'var(--erp-surface-alt)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = 'none'; }}>
                    ⋮
                  </button>
                )}
                {openMenuId === a.AttributeId && (
                  <RowCtxMenu
                    attr={a}
                    groups={groups}
                    anchorRef={{ current: btnRefs.current[a.AttributeId] }}
                    onClose={() => setOpenMenuId(null)}
                    onEdit={() => onSelect(a)}
                    onDelete={() => { onPendingDelete(a.AttributeId); }}
                    onMoveUp={() => onMoveUp(a, idx)}
                    onMoveDown={() => onMoveDown(a, idx)}
                    onPreview={() => onPreview(a)}
                    canMoveUp={idx > 0}
                    canMoveDown={idx < attrs.length - 1}
                  />
                )}
              </td>
            </tr>
          ))}
          {attrs.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 14, color: 'var(--erp-text-muted)', fontSize: 11 }}>{emptyMsg}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const LibraryTab = ({ attributes, groups, industries, onRefresh, taxSystem }) => {
  const [search, setSearch] = useState('');
  const [industryCounts, setIndustryCounts] = useState({});
  const [selectedAttr, setSelectedAttr] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  // Group rename state
  const [editingGroup, setEditingGroup] = useState(null); // { GroupId, Label, Color }
  const [savingGroup, setSavingGroup] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/attribute-engine/matrix`, { headers: hdrs() })
      .then(r => r.json())
      .then(j => {
        if (!j.success) return;
        const counts = {};
        j.mappings.forEach(m => { counts[m.AttributeId] = (counts[m.AttributeId] || 0) + 1; });
        setIndustryCounts(counts);
      })
      .catch(() => {});
  }, [attributes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return attributes;
    return attributes.filter(a =>
      a.Label?.toLowerCase().includes(q) ||
      a.AttributeId?.toLowerCase().includes(q) ||
      a.ControlType?.toLowerCase().includes(q) ||
      groups.find(g => g.GroupId === a.GroupId)?.Label?.toLowerCase().includes(q)
    );
  }, [attributes, search, groups]);

  const attrsByGroup = useMemo(() => {
    const map = new Map();
    groups.forEach(g => map.set(g.GroupId, []));
    filtered.forEach(a => {
      const gid = a.GroupId || '(other)';
      if (!map.has(gid)) map.set(gid, []);
      map.get(gid).push(a);
    });
    return map;
  }, [filtered, groups]);

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/attributes/${id}`, { method: 'DELETE', headers: hdrs() });
      const j = await r.json();
      if (!j.success) { erpToast.error(j.error || 'Delete failed'); setPendingDelete(null); return; }
      erpToast.success(`Attribute "${id}" removed`); setPendingDelete(null); onRefresh();
    } catch { erpToast.error('Network error — delete failed'); setPendingDelete(null); }
  };
  const handleSelect = (a) => setSelectedAttr(prev => prev?.AttributeId === a.AttributeId ? null : a);

  const saveGroupRename = async () => {
    if (!editingGroup) return;
    setSavingGroup(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/groups/${editingGroup.GroupId}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ Label: editingGroup.Label, Color: editingGroup.Color }),
      });
      const j = await r.json();
      if (j.success) { erpToast.success('Group updated'); setEditingGroup(null); onRefresh(); }
      else erpToast.error(j.error || 'Update failed');
    } catch { erpToast.error('Network error'); }
    setSavingGroup(false);
  };

  // Pending group move — show Save/Cancel bar instead of auto-saving
  const [pendingMove, setPendingMove] = useState(null); // { attributeId, oldGroupId, newGroupId, label }
  const [savingMove, setSavingMove] = useState(false);

  const handleGroupChange = (attributeId, newGroupId) => {
    if (!newGroupId) return;
    const attr = attributes.find(a => a.AttributeId === attributeId);
    if (!attr || attr.GroupId === newGroupId) return;
    setPendingMove({ attributeId, oldGroupId: attr.GroupId, newGroupId, label: attr.Label });
  };

  const confirmGroupMove = async () => {
    if (!pendingMove) return;
    setSavingMove(true);
    const { attributeId, newGroupId, label } = pendingMove;
    const attr = attributes.find(a => a.AttributeId === attributeId);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/attributes/${attributeId}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ GroupId: newGroupId, Label: attr?.Label, ControlType: attr?.ControlType }),
      });
      const j = await r.json();
      if (j.success) {
        erpToast.success(`"${label}" moved to ${groups.find(g => g.GroupId === newGroupId)?.Label || newGroupId}`);
        setPendingMove(null);
        onRefresh();
      } else erpToast.error(j.error || 'Move failed');
    } catch { erpToast.error('Network error — move failed'); }
    setSavingMove(false);
  };

  // New Attributes available for opt-in (released after tenant creation)
  const [newAttrsList, setNewAttrsList] = useState([]);
  const [loadingNewAttrs, setLoadingNewAttrs] = useState(false);
  const [optingIn, setOptingIn] = useState(new Set());
  const [showNewAttrs, setShowNewAttrs] = useState(false);

  // Preview state (GetInfo preview modal from context menu)
  const [previewAttr, setPreviewAttr] = useState(null);

  // Move Up / Move Down — swap DisplayOrder with adjacent attr in same group
  const handleMoveInGroup = async (attr, idx, direction) => {
    const groupAttrs = filtered.filter(a => a.GroupId === attr.GroupId).sort((a,b) => (a.DisplayOrder||0)-(b.DisplayOrder||0));
    const pos = groupAttrs.findIndex(a => a.AttributeId === attr.AttributeId);
    const swapPos = direction === 'up' ? pos - 1 : pos + 1;
    if (swapPos < 0 || swapPos >= groupAttrs.length) return;
    const swapAttr = groupAttrs[swapPos];
    const newOrd = swapAttr.DisplayOrder ?? swapPos;
    const curOrd = attr.DisplayOrder ?? pos;
    try {
      await fetch(`${API_URL}/api/attribute-engine/reorder`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ items: [
          { AttributeId: attr.AttributeId, DisplayOrder: newOrd, GroupId: attr.GroupId },
          { AttributeId: swapAttr.AttributeId, DisplayOrder: curOrd, GroupId: swapAttr.GroupId },
        ]}),
      });
      onRefresh();
    } catch { erpToast.error('Move failed'); }
  };

  const loadNewAttrs = async () => {
    setLoadingNewAttrs(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/new-attributes`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setNewAttrsList(j.data || []);
    } catch { erpToast.error('Failed to load new attributes'); }
    setLoadingNewAttrs(false);
  };

  useEffect(() => { loadNewAttrs(); }, []);

  const handleOptIn = async (attributeId) => {
    setOptingIn(p => new Set([...p, attributeId]));
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/tenant-optin`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ attributeIds: [attributeId], optedInBy: 'StudioAdmin' }),
      });
      const j = await r.json();
      if (j.success) {
        erpToast.success(`Opted in to "${attributes.find(a=>a.AttributeId===attributeId)?.Label || attributeId}"`);
        setNewAttrsList(p => p.map(a => a.AttributeId === attributeId ? { ...a, IsOptedIn: 1 } : a));
        onRefresh();
      } else erpToast.error(j.error || 'Opt-in failed');
    } catch { erpToast.error('Network error'); }
    setOptingIn(p => { const n = new Set(p); n.delete(attributeId); return n; });
  };

  // Preset colors for group recoloring
  const GROUP_COLORS = ['#1e40af', '#065f46', '#92400e', '#7c3aed', '#9f1239', '#0369a1', '#374151', '#b45309'];

  return (
    <div style={{ display: 'flex', gap: 16, padding: 'var(--erp-sp-3)' }}>
      <div style={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Attribute add/edit form */}
        <AttributeFormPanel groups={groups} industries={industries} onSaved={onRefresh} selectedAttr={selectedAttr} hideIndustrySection />

        {/* Group Rename Panel */}
        <div style={{ border: '1px solid var(--erp-border)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '6px 10px', background: 'var(--erp-surface-alt)', fontWeight: 700, fontSize: 10, color: 'var(--erp-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Layers size={10} /> Attribute Groups
            <span style={{ fontSize: 9, marginLeft: 4, fontWeight: 400 }}>— rename or recolor</span>
          </div>
          <div style={{ padding: 8 }}>
            {groups.map(g => (
              <div key={g.GroupId} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: g.Color || GROUP_LEGEND[g.GroupId]?.color || '#6b7280', flexShrink: 0 }} />
                {editingGroup?.GroupId === g.GroupId ? (
                  <>
                    <input className="erp-input" style={{ flex: 1, fontSize: 10, padding: '2px 6px', height: 22 }}
                      value={editingGroup.Label}
                      onChange={e => setEditingGroup(prev => ({ ...prev, Label: e.target.value }))}
                      data-testid={`group-rename-input-${g.GroupId}`}
                      autoFocus />
                    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: 80 }}>
                      {GROUP_COLORS.map(c => (
                        <div key={c} onClick={() => setEditingGroup(prev => ({ ...prev, Color: c }))}
                          style={{ width: 12, height: 12, borderRadius: 2, background: c, cursor: 'pointer', border: editingGroup.Color === c ? '2px solid #111' : '1px solid transparent' }} />
                      ))}
                    </div>
                    <button className="erp-btn-icon" onClick={saveGroupRename} disabled={savingGroup} title="Save" style={{ color: 'var(--erp-primary)', fontSize: 10 }}>
                      <Save size={11} />
                    </button>
                    <button className="erp-btn-icon" onClick={() => setEditingGroup(null)} title="Cancel"><X size={11} /></button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 11, fontWeight: 600 }}>{g.Label}</span>
                    <span style={{ fontSize: 9, color: 'var(--erp-text-muted)' }}>{g.Icon}</span>
                    <button className="erp-btn-icon" onClick={() => setEditingGroup({ GroupId: g.GroupId, Label: g.Label, Color: g.Color || GROUP_LEGEND[g.GroupId]?.color || '' })}
                      title="Rename / Recolor" data-testid={`group-edit-${g.GroupId}`}>
                      <Pencil size={10} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{ padding: '4px 10px', background: 'var(--erp-surface-alt)', fontSize: 9, color: 'var(--erp-text-muted)', borderTop: '1px solid var(--erp-border)' }}>
            Saved to: <b>WebApp.AE_AttributeGroups</b> (master DB)
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Search bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '6px 10px', background: 'var(--erp-surface-alt)', borderRadius: 6, border: '1px solid var(--erp-border)' }}>
          <Search size={13} style={{ color: 'var(--erp-text-muted)', flexShrink: 0 }} />
          <input className="erp-input" style={{ border: 'none', background: 'transparent', padding: 0, flex: 1, fontSize: 12 }}
            placeholder="Search by label, ID, control type, group…" value={search}
            onChange={e => setSearch(e.target.value)} data-testid="library-search-input" />
          {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--erp-text-muted)', padding: 0 }}><X size={12} /></button>}
          <span style={{ fontSize: 10, color: 'var(--erp-text-muted)', whiteSpace: 'nowrap' }}>{filtered.length}/{attributes.length}</span>
        </div>

        {/* ── Pending group-move confirmation bar ── */}
        {pendingMove && (
          <div data-testid="pending-group-move-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '8px 12px', background: '#fefce8', border: '1.5px solid #fbbf24', borderRadius: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', flex: 1 }}>
              Move <b>"{pendingMove.label}"</b> from <b>{groups.find(g => g.GroupId === pendingMove.oldGroupId)?.Label || pendingMove.oldGroupId}</b> → <b>{groups.find(g => g.GroupId === pendingMove.newGroupId)?.Label || pendingMove.newGroupId}</b>?
            </span>
            <button onClick={confirmGroupMove} disabled={savingMove} data-testid="confirm-group-move-btn"
              style={{ padding: '4px 14px', borderRadius: 4, border: 'none', background: savingMove ? '#94a3b8' : '#16a34a', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {savingMove ? 'Saving…' : 'Save Move'}
            </button>
            <button onClick={() => setPendingMove(null)} data-testid="cancel-group-move-btn"
              style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#374151' }}>
              Cancel
            </button>
          </div>
        )}

        {/* Dynamic Attribute Group Sections — one per group */}
        {/* New Attributes opt-in panel (for existing tenants) */}
        {newAttrsList.length > 0 && (
          <div style={{ marginBottom: 12, border: '1px solid #fbbf24', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fef9c3', cursor: 'pointer' }}
              onClick={() => setShowNewAttrs(v => !v)}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>
                🆕 {newAttrsList.filter(a=>!a.IsOptedIn).length} New Attributes Available
              </span>
              <span style={{ fontSize: 10, color: '#a16207', marginLeft: 'auto' }}>
                Released since your account was created — opt in to activate
              </span>
              <span style={{ fontSize: 10, color: '#a16207' }}>{showNewAttrs ? '▲' : '▼'}</span>
            </div>
            {showNewAttrs && (
              <div style={{ background: '#fff' }}>
                {newAttrsList.map(a => (
                  <div key={a.AttributeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderTop: '1px solid #fef3c7' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{a.Label}</span>
                    <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>{groups.find(g=>g.GroupId===a.GroupId)?.Label || a.GroupId}</span>
                    <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>{a.ControlType}</span>
                    {a.IsOptedIn ? (
                      <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', padding: '1px 8px', borderRadius: 10, fontWeight: 700 }}>✓ Active</span>
                    ) : (
                      <button disabled={optingIn.has(a.AttributeId)} onClick={() => handleOptIn(a.AttributeId)}
                        data-testid={`optin-${a.AttributeId}`}
                        style={{ fontSize: 10, padding: '2px 10px', border: '1px solid #2563eb', borderRadius: 10, background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>
                        {optingIn.has(a.AttributeId) ? '…' : '+ Opt In'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {groups.map(g => {
          const gAttrs = (attrsByGroup.get(g.GroupId) || []).map(a => applyTaxLabel(a, taxSystem));
          if (search && gAttrs.length === 0) return null;
          const sty = getGroupStyle(g);
          return (
            <div key={g.GroupId} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, padding: '4px 8px', background: sty.bg, border: `1px solid ${sty.border}`, borderRadius: 4 }}>
                {g.Icon && <span style={{ fontSize: 12 }}>{g.Icon}</span>}
                <span style={{ fontSize: 10, fontWeight: 700, color: sty.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>{g.Label}</span>
                <span style={{ fontSize: 9, color: '#fff', background: sty.text, padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>{gAttrs.length}</span>
              </div>
              <AttrTable attrs={gAttrs} groups={groups} industryCounts={industryCounts} selectedAttr={selectedAttr} onSelect={handleSelect} onDelete={handleDelete} pendingDelete={pendingDelete} onPendingDelete={setPendingDelete} onGroupChange={handleGroupChange}
                onMoveUp={(a, idx) => handleMoveInGroup(a, idx, 'up')}
                onMoveDown={(a, idx) => handleMoveInGroup(a, idx, 'down')}
                onPreview={(a) => setPreviewAttr(a)}
                emptyMsg={search ? `No ${g.Label} attributes match "${search}"` : `No ${g.Label} attributes yet`} />
            </div>
          );
        })}

        {/* DB info footer */}
        <div style={{ marginTop: 8, fontSize: 9, color: 'var(--erp-text-muted)', textAlign: 'right' }}>
          Attributes saved to: <b>WebApp.AE_Attributes</b> · Industry mapping: <b>WebApp.AE_IndustryAttributeMap</b> (master DB)
        </div>
      </div>

      {/* GetInfo Preview modal — triggered from row context menu */}
      {previewAttr && previewAttr.SourceTable && (
        <GetInfoPreviewModal
          tableName={previewAttr.SourceTable}
          attrId={previewAttr.AttributeId}
          label={previewAttr.Label}
          onClose={() => setPreviewAttr(null)}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// TIER 2 — Industry Configuration Matrix Grid
// ═══════════════════════════════════════════════════
// INDUSTRY CONFIG MATRIX — Rows = Industries, Columns = Attributes
// Split: Classification | Pricing  ·  Control-type chips  ·  Drag-to-reorder columns
// ═══════════════════════════════════════════════════
const CORE_9 = ['division', 'department', 'category', 'subcategory', 'size', 'design', 'color', 'style', 'uom'];

const MatrixTab = ({ attributes, groups, industries, onRefresh }) => {
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [attrSearch, setAttrSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('classification');
  const [groupFilter, setGroupFilter] = useState('');   // '' = all in section
  const [ctrlFilter, setCtrlFilter] = useState('');
  const [editCell, setEditCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef(null);
  // Drag-to-reorder columns
  const [dragColIdx, setDragColIdx] = useState(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);
  const [localAttrOrder, setLocalAttrOrder] = useState(null);
  // Pending changes buffer — cells staged for save (key = industryId:attrId)
  const [pendingCells, setPendingCells] = useState({}); // { [key]: { industryId, attrId, active } }
  const [saving, setSaving] = useState(false);
  // Industry Label Override side panel
  const [labelPanel, setLabelPanel] = useState(null); // { attr: {AttributeId, Label}, edits: {} }
  const [savingLabels, setSavingLabels] = useState(false);
  // Add Industry
  const [showAddIndustry, setShowAddIndustry] = useState(false);
  const [newInd, setNewInd] = useState({ IndustryId: '', IndustryName: '', Icon: '', CategoryGroup: '' });
  const [savingInd, setSavingInd] = useState(false);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/matrix`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        const map = {};
        j.mappings.forEach(m => {
          if (!map[m.IndustryId]) map[m.IndustryId] = {};
          map[m.IndustryId][m.AttributeId] = { active: true, customLabel: m.CustomLabel || '', isRequired: !!m.IsRequired };
        });
        setMatrix(map);
      }
    } catch { erpToast.error('Failed to load matrix'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  const commitLabel = useCallback(async () => {
    if (!editCell) return;
    const { industryId, attrId } = editCell;
    const attr = attributes.find(a => a.AttributeId === attrId);
    const isDefaultLabel = editValue.trim() === attr?.Label;
    const customLabel = isDefaultLabel ? '' : editValue.trim();
    setMatrix(prev => ({
      ...prev,
      [industryId]: { ...prev[industryId], [attrId]: { ...(prev[industryId]?.[attrId] || {}), customLabel } }
    }));
    setEditCell(null);
    try {
      await fetch(`${API_URL}/api/attribute-engine/matrix/cell`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ industryId, attributeId: attrId, active: true, customLabel }),
      });
    } catch { erpToast.error('Label save failed'); }
  }, [editCell, editValue, attributes]);

  useEffect(() => {
    if (!editCell) return;
    const onClickOut = (e) => { if (editRef.current && !editRef.current.contains(e.target)) commitLabel(); };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, [editCell, editValue, commitLabel]);

  const setSaved = () => {}; // no-op — replaced by pendingCells + saveAllPending

  const toggleCell = (industryId, attrId) => {
    const current = matrix[industryId]?.[attrId];
    const active = !current?.active;
    setMatrix(prev => ({
      ...prev,
      [industryId]: { ...prev[industryId], [attrId]: { ...(prev[industryId]?.[attrId] || {}), active } }
    }));
    const key = `${industryId}:${attrId}`;
    setPendingCells(prev => ({ ...prev, [key]: { industryId, attrId, active } }));
  };

  const selectAllForAttr = (attrId) => {
    const allActive = filteredIndustries.every(i => matrix[i.IndustryId]?.[attrId]?.active);
    const newActive = !allActive;
    setMatrix(prev => {
      const updated = { ...prev };
      filteredIndustries.forEach(ind => {
        if (!updated[ind.IndustryId]) updated[ind.IndustryId] = {};
        updated[ind.IndustryId] = { ...updated[ind.IndustryId], [attrId]: { ...(updated[ind.IndustryId][attrId] || {}), active: newActive } };
      });
      return updated;
    });
    setPendingCells(prev => {
      const next = { ...prev };
      filteredIndustries.forEach(ind => { next[`${ind.IndustryId}:${attrId}`] = { industryId: ind.IndustryId, attrId, active: newActive }; });
      return next;
    });
  };

  const selectAllForIndustry = (industryId) => {
    const allActive = displayAttrs.every(a => matrix[industryId]?.[a.AttributeId]?.active);
    const newActive = !allActive;
    setMatrix(prev => {
      const updated = { ...prev, [industryId]: { ...(prev[industryId] || {}) } };
      displayAttrs.forEach(a => {
        updated[industryId][a.AttributeId] = { ...(updated[industryId][a.AttributeId] || {}), active: newActive };
      });
      return updated;
    });
    setPendingCells(prev => {
      const next = { ...prev };
      displayAttrs.forEach(a => { next[`${industryId}:${a.AttributeId}`] = { industryId, attrId: a.AttributeId, active: newActive }; });
      return next;
    });
  };

  const saveAllPending = async () => {
    const items = Object.values(pendingCells);
    if (!items.length) return;
    setSaving(true);
    try {
      await Promise.all(items.map(({ industryId, attrId, active }) =>
        fetch(`${API_URL}/api/attribute-engine/matrix/cell`, {
          method: 'PUT', headers: hdrs(),
          body: JSON.stringify({ industryId, attributeId: attrId, active }),
        })
      ));
      erpToast.success(`${items.length} change${items.length !== 1 ? 's' : ''} saved`);
      setPendingCells({});
    } catch { erpToast.error('Some saves failed'); }
    setSaving(false);
  };

  const startEditLabel = (industryId, attrId, e) => {
    e.stopPropagation();
    const current = matrix[industryId]?.[attrId];
    if (!current?.active) return;
    const attr = attributes.find(a => a.AttributeId === attrId);
    setEditCell({ industryId, attrId });
    setEditValue(current?.customLabel || attr?.Label || '');
    setTimeout(() => editRef.current?.querySelector('input')?.focus(), 50);
  };

  // ── Section split: Classification vs Pricing ──
  const orderedAttrs = useMemo(() => {
    const base = localAttrOrder || [...attributes].sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));
    return base;
  }, [attributes, localAttrOrder]);

  const sectionBase = useMemo(() => {
    if (sectionFilter === 'classification') return orderedAttrs.filter(a => !PRICING_GROUPS.has(a.GroupId) && !TAX_GROUPS.has(a.GroupId));
    if (sectionFilter === 'pricing') return orderedAttrs.filter(a => PRICING_GROUPS.has(a.GroupId));
    if (sectionFilter === 'tax') return orderedAttrs.filter(a => TAX_GROUPS.has(a.GroupId));
    return orderedAttrs;
  }, [orderedAttrs, sectionFilter]);

  // ── Control-type chip filter + attribute text search ──
  const displayAttrs = useMemo(() => {
    let base = sectionBase;
    if (groupFilter) base = base.filter(a => a.GroupId === groupFilter);
    if (ctrlFilter) base = base.filter(a => a.ControlType === ctrlFilter);
    if (attrSearch.trim()) {
      const q = attrSearch.trim().toLowerCase();
      base = base.filter(a => a.Label?.toLowerCase().includes(q) || a.AttributeId?.toLowerCase().includes(q));
    }
    return base;
  }, [sectionBase, ctrlFilter, attrSearch, groupFilter]);

  const availableCtrlTypes = useMemo(() =>
    Array.from(new Set(sectionBase.map(a => a.ControlType).filter(Boolean))).sort(),
    [sectionBase]);

  const filteredIndustries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return industries;
    return industries.filter(i =>
      i.IndustryName.toLowerCase().includes(q) ||
      (i.CategoryGroup || '').toLowerCase().includes(q) ||
      i.IndustryId.toLowerCase().includes(q)
    );
  }, [industries, search]);

  const industryGroups = useMemo(() => {
    const map = new Map();
    filteredIndustries.forEach(i => {
      const g = i.CategoryGroup || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(i);
    });
    return Array.from(map.entries());
  }, [filteredIndustries]);

  // ── Drag-to-reorder columns ──
  const handleColDragStart = (e, idx) => {
    setDragColIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };
  const handleColDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragColIdx) setDragOverColIdx(idx);
  };
  const handleColDrop = async (e, targetIdx) => {
    e.preventDefault();
    if (dragColIdx === null || dragColIdx === targetIdx) {
      setDragColIdx(null); setDragOverColIdx(null); return;
    }
    // Reorder sectionBase (not just displayAttrs — preserve filtered-out attrs positions)
    const otherAttrs = orderedAttrs.filter(a => {
      if (sectionFilter === 'classification') return PRICING_GROUPS.has(a.GroupId) || TAX_GROUPS.has(a.GroupId);
      if (sectionFilter === 'tax') return !TAX_GROUPS.has(a.GroupId);
      return !PRICING_GROUPS.has(a.GroupId); // pricing section
    });
    const newSection = [...sectionBase];
    const [moved] = newSection.splice(dragColIdx, 1);
    newSection.splice(targetIdx, 0, moved);
    const classAttrsOrd = orderedAttrs.filter(a => !PRICING_GROUPS.has(a.GroupId) && !TAX_GROUPS.has(a.GroupId));
    const pricingAttrsOrd = orderedAttrs.filter(a => PRICING_GROUPS.has(a.GroupId));
    const taxAttrsOrd = orderedAttrs.filter(a => TAX_GROUPS.has(a.GroupId));
    const newAll = sectionFilter === 'pricing'
      ? [...classAttrsOrd, ...newSection, ...taxAttrsOrd]
      : sectionFilter === 'tax'
        ? [...classAttrsOrd, ...pricingAttrsOrd, ...newSection]
        : [...newSection, ...pricingAttrsOrd, ...taxAttrsOrd];
    const withOrder = newAll.map((a, i) => ({ ...a, DisplayOrder: i * 10 }));
    setLocalAttrOrder(withOrder);
    setDragColIdx(null); setDragOverColIdx(null);
    try {
      await fetch(`${API_URL}/api/attribute-engine/reorder`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ items: withOrder.map(a => ({ AttributeId: a.AttributeId, DisplayOrder: a.DisplayOrder, GroupId: a.GroupId })) }),
      });
      erpToast.success('Column order saved');
      onRefresh?.();
    } catch { erpToast.error('Reorder failed'); setLocalAttrOrder(null); }
  };
  const handleColDragEnd = () => { setDragColIdx(null); setDragOverColIdx(null); };

  // Open industry label override side panel for a specific attribute
  const openLabelPanel = (attr) => {
    const edits = {};
    industries.forEach(ind => {
      const cell = matrix[ind.IndustryId]?.[attr.AttributeId];
      if (cell?.active) edits[ind.IndustryId] = cell.customLabel || '';
    });
    setLabelPanel({ attr, edits });
  };

  const saveLabelPanel = async () => {
    if (!labelPanel) return;
    setSavingLabels(true);
    const { attr, edits } = labelPanel;
    const activeInds = industries.filter(i => matrix[i.IndustryId]?.[attr.AttributeId]?.active);
    try {
      await Promise.all(activeInds.map(ind =>
        fetch(`${API_URL}/api/attribute-engine/matrix/cell`, {
          method: 'PUT', headers: hdrs(),
          body: JSON.stringify({ industryId: ind.IndustryId, attributeId: attr.AttributeId, active: true, customLabel: edits[ind.IndustryId] || '' }),
        })
      ));
      // Update local matrix
      setMatrix(prev => {
        const updated = { ...prev };
        activeInds.forEach(ind => {
          if (!updated[ind.IndustryId]) updated[ind.IndustryId] = {};
          updated[ind.IndustryId][attr.AttributeId] = { ...updated[ind.IndustryId][attr.AttributeId], customLabel: edits[ind.IndustryId] || '' };
        });
        return updated;
      });
      erpToast.success('Labels saved');
      setSaved();
      setLabelPanel(null);
    } catch { erpToast.error('Save failed'); }
    setSavingLabels(false);
  };

  const classCount = useMemo(() => orderedAttrs.filter(a => !PRICING_GROUPS.has(a.GroupId) && !TAX_GROUPS.has(a.GroupId)).length, [orderedAttrs]);
  const priceCount = useMemo(() => orderedAttrs.filter(a => PRICING_GROUPS.has(a.GroupId)).length, [orderedAttrs]);
  const taxCount = useMemo(() => orderedAttrs.filter(a => TAX_GROUPS.has(a.GroupId)).length, [orderedAttrs]);

  const handleAddIndustry = async () => {
    if (!newInd.IndustryId.trim() || !newInd.IndustryName.trim()) {
      erpToast.error('Industry ID and Name are required');
      return;
    }
    setSavingInd(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/industries`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({
          IndustryId: newInd.IndustryId.trim(),
          IndustryName: newInd.IndustryName.trim(),
          Icon: newInd.Icon.trim() || null,
          CategoryGroup: newInd.CategoryGroup.trim() || null,
        }),
      });
      const j = await r.json();
      if (j.success) {
        erpToast.success(`Industry "${newInd.IndustryName}" added with 9 default attributes`);
        setNewInd({ IndustryId: '', IndustryName: '', Icon: '', CategoryGroup: '' });
        setShowAddIndustry(false);
        onRefresh?.();
      } else {
        erpToast.error(j.error || 'Failed to add industry');
      }
    } catch { erpToast.error('Network error'); }
    setSavingInd(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading matrix…</div>;

  return (
    <div style={{ padding: 'var(--erp-sp-3)' }} data-testid="matrix-tab">

      {/* ── Section Selector + Save status ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { id: 'classification', label: 'Classification & Style', count: classCount, bg: '#1e3a8a', light: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
          { id: 'pricing', label: 'Pricing', count: priceCount, bg: '#7c2d12', light: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
          { id: 'tax', label: 'Tax & Compliance', count: taxCount, bg: '#7f1d1d', light: '#fef2f2', border: '#fecaca', text: '#991b1b' },
        ].map(s => (
          <button key={s.id}
            style={{
              padding: '5px 14px', border: `1.5px solid ${sectionFilter === s.id ? s.bg : s.border}`,
              borderRadius: 6, fontSize: 11, fontWeight: sectionFilter === s.id ? 700 : 500, cursor: 'pointer',
              background: sectionFilter === s.id ? s.bg : s.light,
              color: sectionFilter === s.id ? '#fff' : s.text,
            }}
            onClick={() => { setSectionFilter(s.id); setCtrlFilter(''); setGroupFilter(''); }}
            data-testid={`section-tab-${s.id}`}
          >
            {s.label}
            <span style={{ marginLeft: 6, fontSize: 9, background: sectionFilter === s.id ? 'rgba(255,255,255,0.2)' : s.bg, color: '#fff', padding: '1px 5px', borderRadius: 8 }}>{s.count}</span>
          </button>
        ))}
        {/* ── Group filter dropdown — shows all individual groups in the current section ── */}
        {(() => {
          const sectionGroups = groups.filter(g => {
            if (sectionFilter === 'pricing') return PRICING_GROUPS.has(g.GroupId);
            if (sectionFilter === 'tax') return TAX_GROUPS.has(g.GroupId);
            return !PRICING_GROUPS.has(g.GroupId) && !TAX_GROUPS.has(g.GroupId);
          });
          if (sectionGroups.length <= 1) return null;
          return (
            <select
              value={groupFilter}
              onChange={e => { setGroupFilter(e.target.value); setCtrlFilter(''); }}
              data-testid="group-filter-select"
              style={{ fontSize: 11, padding: '4px 8px', border: '1.5px solid var(--erp-border)', borderRadius: 6, background: groupFilter ? '#1e3a8a' : 'var(--erp-surface)', color: groupFilter ? '#fff' : 'var(--erp-text)', cursor: 'pointer', outline: 'none' }}
            >
              <option value="">All Groups ({sectionGroups.length})</option>
              {sectionGroups.map(g => {
                const cnt = orderedAttrs.filter(a => a.GroupId === g.GroupId).length;
                return <option key={g.GroupId} value={g.GroupId}>{g.Icon ? `${g.Icon} ` : ''}{g.Label} ({cnt})</option>;
              })}
            </select>
          );
        })()}
        <div style={{ flex: 1 }} />
        {/* Pending changes Save button */}
        {Object.keys(pendingCells).length > 0 && (
          <button
            onClick={saveAllPending}
            disabled={saving}
            data-testid="matrix-save-btn"
            style={{
              padding: '5px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              background: saving ? '#94a3b8' : '#16a34a', color: '#fff', border: 'none',
            }}
          >
            {saving ? 'Saving…' : `Save ${Object.keys(pendingCells).length} Change${Object.keys(pendingCells).length !== 1 ? 's' : ''}`}
          </button>
        )}
        <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>
          {filteredIndustries.length} industries · {displayAttrs.length} shown
        </span>
      </div>

      {/* ── Control Type Chips ── */}
      {availableCtrlTypes.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: 'var(--erp-text-muted)', marginRight: 2, whiteSpace: 'nowrap' }}>Control type:</span>
          {['', ...availableCtrlTypes].map(ct => (
            <button key={ct}
              style={{
                padding: '2px 8px', borderRadius: 10, fontSize: 10, cursor: 'pointer', border: '1px solid',
                borderColor: ctrlFilter === ct ? 'var(--erp-primary)' : 'var(--erp-border)',
                background: ctrlFilter === ct ? 'var(--erp-primary)' : 'var(--erp-surface-alt)',
                color: ctrlFilter === ct ? '#fff' : 'var(--erp-text)',
                fontWeight: ctrlFilter === ct ? 700 : 400,
              }}
              onClick={() => setCtrlFilter(ct)}
              data-testid={`ctrl-chip-${ct || 'all'}`}
            >
              {ct || 'All'}
            </button>
          ))}
        </div>
      )}

      {/* ── Search Row ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 150px', background: 'var(--erp-surface-alt)', border: '1px solid var(--erp-border)', borderRadius: 6, padding: '4px 10px' }}>
          <Search size={12} style={{ color: 'var(--erp-text-muted)' }} />
          <input className="erp-input" style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 11 }}
            placeholder="Filter industries…" value={search} onChange={e => setSearch(e.target.value)}
            data-testid="matrix-industry-search" />
          {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}><X size={11} /></button>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 150px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px' }}>
          <Search size={12} style={{ color: '#3b82f6' }} />
          <input className="erp-input" style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 11 }}
            placeholder="Search attributes (columns)…" value={attrSearch} onChange={e => setAttrSearch(e.target.value)}
            data-testid="matrix-attr-search" />
          {attrSearch && <button onClick={() => setAttrSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}><X size={11} /></button>}
        </div>
        <button onClick={() => setShowAddIndustry(p => !p)} data-testid="add-industry-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: showAddIndustry ? '#f0fdf4' : '#fff', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#15803d', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
          + Add Industry
        </button>
      </div>

      {/* ── Add Industry Inline Form ── */}
      {showAddIndustry && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', width: '100%', marginBottom: 2 }}>New Industry — fills 9 default classification attributes automatically</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>Industry ID *</label>
            <input className="erp-input" value={newInd.IndustryId} onChange={e => setNewInd(p => ({...p, IndustryId: e.target.value}))}
              placeholder="Pharmacy" style={{ fontSize: 11, padding: '3px 6px', width: 120 }} data-testid="add-industry-id-input" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 180px' }}>
            <label style={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>Industry Name *</label>
            <input className="erp-input" value={newInd.IndustryName} onChange={e => setNewInd(p => ({...p, IndustryName: e.target.value}))}
              placeholder="Pharmacy & Healthcare" style={{ fontSize: 11, padding: '3px 6px' }} data-testid="add-industry-name-input" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>Icon</label>
            <input className="erp-input" value={newInd.Icon} onChange={e => setNewInd(p => ({...p, Icon: e.target.value}))}
              placeholder="💊" style={{ fontSize: 11, padding: '3px 6px', width: 60 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>Category Group</label>
            <input className="erp-input" value={newInd.CategoryGroup} onChange={e => setNewInd(p => ({...p, CategoryGroup: e.target.value}))}
              placeholder="Healthcare" style={{ fontSize: 11, padding: '3px 6px', width: 120 }} />
          </div>
          <button onClick={handleAddIndustry} disabled={savingInd || !newInd.IndustryId.trim() || !newInd.IndustryName.trim()}
            data-testid="add-industry-save-btn"
            style={{ padding: '5px 14px', background: savingInd ? '#aaa' : '#15803d', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            {savingInd ? 'Saving…' : 'Add Industry'}
          </button>
          <button onClick={() => { setShowAddIndustry(false); setNewInd({ IndustryId: '', IndustryName: '', Icon: '', CategoryGroup: '' }); }}
            style={{ padding: '5px 10px', background: '#fff', color: '#6b7280', border: '1px solid var(--erp-border)', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}

      {/* ── Matrix Table ── */}
      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 520, border: '1px solid var(--erp-border)', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: '100%', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 180, minWidth: 180 }} />
            {displayAttrs.map(a => <col key={a.AttributeId} style={{ width: 90, minWidth: 80 }} />)}
          </colgroup>
          <thead>
            <tr style={{ background: 'var(--erp-surface-alt)', position: 'sticky', top: 0, zIndex: 2 }}>
              <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 700, borderBottom: '2px solid var(--erp-border)', borderRight: '2px solid var(--erp-border)', background: 'var(--erp-surface-alt)', position: 'sticky', left: 0, zIndex: 3 }}>
                Industry
              </th>
              {displayAttrs.map((a, idx) => {
                const allActive = filteredIndustries.length > 0 && filteredIndustries.every(i => matrix[i.IndustryId]?.[a.AttributeId]?.active);
                const someActive = filteredIndustries.some(i => matrix[i.IndustryId]?.[a.AttributeId]?.active);
                const isDragging = dragColIdx === idx;
                const isDragOver = dragOverColIdx === idx;
                return (
                  <th key={a.AttributeId}
                    draggable
                    onDragStart={e => handleColDragStart(e, idx)}
                    onDragOver={e => handleColDragOver(e, idx)}
                    onDrop={e => handleColDrop(e, idx)}
                    onDragEnd={handleColDragEnd}
                    style={{
                      padding: '5px 4px', fontWeight: 600, borderBottom: '2px solid var(--erp-border)',
                      borderRight: '1px solid var(--erp-border)', borderLeft: isDragOver ? '2px solid var(--erp-primary)' : undefined,
                      background: isDragging ? '#dbeafe' : allActive ? '#eff6ff' : someActive ? '#f8faff' : 'var(--erp-surface-alt)',
                      textAlign: 'center', maxWidth: 90, cursor: 'grab', opacity: isDragging ? 0.5 : 1,
                      userSelect: 'none',
                    }}
                    title={`${a.Label} (${a.AttributeId}) · ${a.ControlType}\nClick header checkbox to select all · Drag to reorder\nRight-click or use label icon to set industry labels`}
                    onClick={() => selectAllForAttr(a.AttributeId)}
                    data-testid={`matrix-col-${a.AttributeId}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                      <GripVertical size={9} style={{ color: 'var(--erp-text-muted)', opacity: 0.5, flexShrink: 0 }} />
                      <button onClick={e => { e.stopPropagation(); openLabelPanel(a); }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 1, color: '#3b82f6', opacity: 0.6 }}
                        title="Set industry label overrides">
                        <Pencil size={8} />
                      </button>
                    </div>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{a.Label}</div>
                    <div style={{ fontSize: 8, color: 'var(--erp-text-muted)', fontFamily: 'var(--erp-font-mono)', marginBottom: 2 }}>{a.ControlType}</div>
                    <div style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${allActive ? 'var(--erp-primary)' : someActive ? '#93c5fd' : 'var(--erp-border)'}`, background: allActive ? 'var(--erp-primary)' : someActive ? '#dbeafe' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                      {allActive && <Check size={9} color="#fff" strokeWidth={3} />}
                      {someActive && !allActive && <span style={{ width: 6, height: 2, background: '#3b82f6', borderRadius: 1 }} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {industryGroups.map(([categoryGroup, inds]) => (
              <React.Fragment key={categoryGroup}>
                <tr>
                  <td colSpan={displayAttrs.length + 1}
                    style={{ padding: '4px 10px', background: 'var(--erp-surface)', fontWeight: 700, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--erp-text-muted)', borderBottom: '1px solid var(--erp-border)' }}>
                    {categoryGroup}
                  </td>
                </tr>
                {inds.map(ind => (
                  <tr key={ind.IndustryId} style={{ borderBottom: '1px solid var(--erp-border)' }}
                    data-testid={`matrix-row-${ind.IndustryId}`}>
                    <td style={{
                      padding: '5px 10px', fontWeight: 600, borderRight: '2px solid var(--erp-border)',
                      background: 'var(--erp-surface)', position: 'sticky', left: 0, zIndex: 1, maxWidth: 180,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer',
                    }} title={`${ind.IndustryName} — Click to select/deselect all attributes`}
                      onClick={() => selectAllForIndustry(ind.IndustryId)}
                      data-testid={`matrix-row-header-${ind.IndustryId}`}>
                      <span style={{ fontSize: 12, marginRight: 4 }}>{ind.Icon}</span>
                      {ind.IndustryName}
                    </td>
                    {displayAttrs.map(attr => {
                      const current = matrix[ind.IndustryId]?.[attr.AttributeId];
                      const isActive = !!current?.active;
                      const hasCustomLabel = !!current?.customLabel;
                      const displayLabel = current?.customLabel || attr.Label;
                      const isEditing = editCell?.industryId === ind.IndustryId && editCell?.attrId === attr.AttributeId;
                      return (
                        <td key={attr.AttributeId}
                          onClick={() => !isEditing && toggleCell(ind.IndustryId, attr.AttributeId)}
                          style={{ padding: '4px 6px', borderRight: '1px solid var(--erp-border)', textAlign: 'center', cursor: 'pointer', background: isActive ? (hasCustomLabel ? '#f0fdf4' : 'transparent') : 'transparent', verticalAlign: 'middle' }}
                          data-testid={`matrix-cell-${ind.IndustryId}-${attr.AttributeId}`}
                        >
                          {isEditing ? (
                            <div ref={editRef} style={{ display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>
                              <input value={editValue} onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') setEditCell(null); }}
                                style={{ width: 70, fontSize: 9, padding: '1px 4px', border: '1px solid var(--erp-primary)', borderRadius: 3, outline: 'none' }} />
                              <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--erp-primary)', padding: 0 }} onClick={commitLabel}><Check size={10} /></button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <div style={{
                                width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${isActive ? 'var(--erp-primary)' : 'var(--erp-border)'}`,
                                background: isActive ? 'var(--erp-primary)' : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {isActive && <Check size={9} color="#fff" strokeWidth={3} />}
                              </div>
                              {isActive && (
                                <span
                                  style={{ fontSize: 9, color: hasCustomLabel ? 'var(--erp-success)' : 'var(--erp-text-muted)', maxWidth: 55, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }}
                                  title={hasCustomLabel ? `Custom: ${displayLabel}\nClick to edit` : 'Click to set custom label'}
                                  onClick={(e) => startEditLabel(ind.IndustryId, attr.AttributeId, e)}
                                  data-testid={`matrix-label-${ind.IndustryId}-${attr.AttributeId}`}
                                >
                                  {hasCustomLabel ? displayLabel : <Pencil size={9} style={{ opacity: 0.4 }} />}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 8, fontSize: 10, color: 'var(--erp-text-muted)' }}>
        <b>Drag</b> column header to reorder · <b>Click header</b> to enable all industries · <b>Click industry row</b> to enable all attributes · <b>Click cell</b> to toggle · <b>Click label</b> to set custom name · All changes auto-save to <b>WebApp.AE_IndustryAttributeMap</b>
      </div>

      {/* ── Industry Label Override side panel ── */}
      {labelPanel && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 320, height: '100vh', background: 'var(--erp-surface)', borderLeft: '2px solid var(--erp-primary)', zIndex: 50, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
          data-testid="label-override-panel">
          {/* Panel header */}
          <div style={{ padding: '12px 16px', background: 'var(--erp-primary)', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={13} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>Industry Label Overrides</div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>"{labelPanel.attr.Label}" (ID: {labelPanel.attr.AttributeId})</div>
            </div>
            <button onClick={() => setLabelPanel(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.2)', cursor: 'pointer', borderRadius: 4, padding: 4, color: '#fff' }}><X size={14} /></button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--erp-text-muted)', padding: '8px 16px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-surface-alt)' }}>
            Set custom labels for each industry. Leave blank to use the default label "<b>{labelPanel.attr.Label}</b>".
            <br /><span style={{ fontSize: 9 }}>Only industries with this attribute enabled are shown.</span>
          </div>
          {/* Panel industry list */}
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
            {industries.filter(i => matrix[i.IndustryId]?.[labelPanel.attr.AttributeId]?.active).length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', fontSize: 11, color: 'var(--erp-text-muted)' }}>
                No industries have this attribute enabled yet.<br />Enable some industries first from the matrix.
              </div>
            ) : industries.filter(i => matrix[i.IndustryId]?.[labelPanel.attr.AttributeId]?.active).map(ind => (
              <div key={ind.IndustryId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 16px', borderBottom: '1px solid var(--erp-border-light)' }}>
                <span style={{ fontSize: 12, width: 20 }}>{ind.Icon}</span>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 600 }}>{ind.IndustryName}</span>
                <input className="erp-input"
                  style={{ width: 110, fontSize: 10, padding: '2px 6px', height: 22 }}
                  placeholder={labelPanel.attr.Label}
                  value={labelPanel.edits[ind.IndustryId] || ''}
                  onChange={e => setLabelPanel(prev => ({ ...prev, edits: { ...prev.edits, [ind.IndustryId]: e.target.value } }))}
                  data-testid={`label-panel-input-${ind.IndustryId}`}
                />
              </div>
            ))}
          </div>
          {/* Panel footer with save */}
          <div style={{ padding: '12px 16px', borderTop: '2px solid var(--erp-border)', background: 'var(--erp-surface-alt)', display: 'flex', gap: 8 }}>
            <button className="erp-btn erp-btn-primary" style={{ flex: 1, fontSize: 11 }}
              onClick={saveLabelPanel} disabled={savingLabels} data-testid="save-labels-btn">
              <Save size={12} /> {savingLabels ? 'Saving…' : 'Save All Labels'}
            </button>
            <button className="erp-btn erp-btn-ghost" style={{ fontSize: 11 }} onClick={() => setLabelPanel(null)}>Cancel</button>
          </div>
          <div style={{ padding: '4px 16px', background: 'var(--erp-surface-alt)', fontSize: 9, color: 'var(--erp-text-muted)', textAlign: 'center' }}>
            Saved to: <b>WebApp.AE_IndustryAttributeMap.CustomLabel</b> (master DB)
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// ATTRIBUTE ORDERING — drag-and-drop DisplayOrder editor
// ═══════════════════════════════════════════════════
const GROUP_COLORS = {
  grp_classification: '#1e40af',
  grp_style: '#7c3aed',
  grp_physical: '#065f46',
  grp_compliance: '#92400e',
  grp_pricing: '#991b1b',
};

const OrderingTab = ({ attributes, groups, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  // Track original state so we can diff and only save changed items
  const originalRef = useRef({});

  useEffect(() => {
    const sorted = [...attributes].sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));
    setItems(sorted);
    setHasChanges(false);
    // Snapshot original DisplayOrder + GroupId for each attr
    const snap = {};
    sorted.forEach(a => { snap[a.AttributeId] = { DisplayOrder: a.DisplayOrder, GroupId: a.GroupId }; });
    originalRef.current = snap;
  }, [attributes]);

  const orderedGroups = useMemo(() => {
    const map = new Map();
    groups.forEach(g => map.set(g.GroupId, { ...g, attrs: [] }));
    map.set('(none)', { GroupId: '(none)', Label: 'Ungrouped', Icon: '', attrs: [] });
    items.forEach(item => {
      const gid = item.GroupId || '(none)';
      if (!map.has(gid)) map.set(gid, { GroupId: gid, Label: gid, Icon: '', attrs: [] });
      map.get(gid).attrs.push(item);
    });
    return Array.from(map.values());
  }, [items, groups]);

  const onDragStart = (e, id) => { setDraggedId(id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); };
  const onDragOver = (e, targetId) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (targetId !== draggedId) setDragOverId(targetId); };
  const onDrop = (e, targetId, targetGroupId) => {
    e.preventDefault(); e.stopPropagation();
    if (!draggedId || draggedId === targetId) { setDraggedId(null); setDragOverId(null); return; }
    setItems(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(i => i.AttributeId === draggedId);
      if (fromIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      moved.GroupId = targetGroupId;
      const toIdx = arr.findIndex(i => i.AttributeId === targetId);
      arr.splice(toIdx >= 0 ? toIdx : arr.length, 0, moved);
      return arr.map((item, i) => ({ ...item, DisplayOrder: i + 1 }));
    });
    setDraggedId(null); setDragOverId(null); setHasChanges(true);
  };
  const onDropOnGroup = (e, groupId) => {
    e.preventDefault(); e.stopPropagation();
    if (!draggedId) return;
    setItems(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(i => i.AttributeId === draggedId);
      if (fromIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      moved.GroupId = groupId === '(none)' ? null : groupId;
      let insertAt = -1;
      arr.forEach((item, i) => { if ((item.GroupId || '(none)') === groupId) insertAt = i + 1; });
      arr.splice(insertAt >= 0 ? insertAt : arr.length, 0, moved);
      return arr.map((item, i) => ({ ...item, DisplayOrder: i + 1 }));
    });
    setDraggedId(null); setDragOverId(null); setHasChanges(true);
  };
  const onDragEnd = () => { setDraggedId(null); setDragOverId(null); };

  const saveOrder = async () => {
    setSaving(true);
    try {
      // Only send items whose DisplayOrder or GroupId actually changed — avoids sending 163 items
      const orig = originalRef.current;
      const changed = items.filter(a => {
        const o = orig[a.AttributeId];
        return !o || o.DisplayOrder !== a.DisplayOrder || o.GroupId !== a.GroupId;
      });
      if (!changed.length) { erpToast.info('No changes to save'); setSaving(false); return; }
      const payload = changed.map(a => ({ AttributeId: a.AttributeId, DisplayOrder: a.DisplayOrder, GroupId: a.GroupId || null }));
      const r = await fetch(`${API_URL}/api/attribute-engine/reorder`, { method: 'PUT', headers: hdrs(), body: JSON.stringify({ items: payload }) });
      const j = await r.json();
      if (j.success) {
        erpToast.success(`Order saved — ${j.updated} attribute${j.updated !== 1 ? 's' : ''} updated`);
        setHasChanges(false);
        // Update snapshot so next save diffs from new baseline
        changed.forEach(a => { orig[a.AttributeId] = { DisplayOrder: a.DisplayOrder, GroupId: a.GroupId }; });
        onRefresh();
      } else erpToast.error(j.error || 'Save failed');
    } catch { erpToast.error('Network error'); }
    setSaving(false);
  };

  return (
    <div style={{ padding: 'var(--erp-sp-3)' }} data-testid="ordering-tab">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '8px 12px', background: 'var(--erp-surface-alt)', borderRadius: 6, border: '1px solid var(--erp-border)' }}>
        <GripVertical size={14} style={{ color: 'var(--erp-text-muted)', flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 11, color: 'var(--erp-text-muted)' }}>
          Drag rows to reorder. Dropping into a different group section also changes the attribute's group.
          {hasChanges && <strong style={{ color: 'var(--erp-primary)', marginLeft: 8 }}>— Unsaved changes</strong>}
        </span>
        {hasChanges && (
          <>
            <button className="erp-btn erp-btn-ghost" onClick={() => { setItems([...attributes].sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))); setHasChanges(false); }} style={{ fontSize: 11 }} data-testid="reset-order-btn">Reset</button>
            <button className="erp-btn erp-btn-primary" onClick={saveOrder} disabled={saving} style={{ fontSize: 11 }} data-testid="save-order-btn">{saving ? 'Saving…' : 'Save Order'}</button>
          </>
        )}
      </div>
      {orderedGroups.map(group => {
        const color = GROUP_COLORS[group.GroupId] || '#475569';
        return (
          <div key={group.GroupId} style={{ marginBottom: 14 }} onDragOver={e => e.preventDefault()} onDrop={e => onDropOnGroup(e, group.GroupId)} data-testid={`order-group-${group.GroupId}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: 'var(--erp-surface-alt)', borderLeft: `3px solid ${color}`, borderRadius: '2px 0 0 0', marginBottom: 0, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {group.Icon && <span style={{ fontSize: 13 }}>{group.Icon}</span>}
              {group.Label}
              <span style={{ fontWeight: 400, color: 'var(--erp-text-muted)', fontSize: 10, marginLeft: 4 }}>{group.attrs.length} attribute{group.attrs.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ border: '1px solid var(--erp-border)', borderTop: 'none', borderRadius: '0 0 4px 4px', minHeight: 36 }}>
              {group.attrs.length === 0 ? (
                <div style={{ padding: 10, textAlign: 'center', fontSize: 11, color: 'var(--erp-text-muted)', fontStyle: 'italic' }}>Drop attributes here</div>
              ) : group.attrs.map((attr, idx) => {
                const isDragging = draggedId === attr.AttributeId;
                const isOver = dragOverId === attr.AttributeId;
                return (
                  <div key={attr.AttributeId} draggable
                    onDragStart={e => onDragStart(e, attr.AttributeId)}
                    onDragOver={e => onDragOver(e, attr.AttributeId)}
                    onDrop={e => onDrop(e, attr.AttributeId, group.GroupId)}
                    onDragEnd={onDragEnd}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderBottom: idx < group.attrs.length - 1 ? '1px solid var(--erp-border)' : 'none', borderTop: isOver ? `2px solid ${color}` : undefined, cursor: 'grab', opacity: isDragging ? 0.3 : 1, background: isDragging ? 'var(--erp-surface-alt)' : isOver ? `${color}12` : 'transparent', userSelect: 'none', fontSize: 12 }}
                    data-testid={`order-attr-${attr.AttributeId}`}>
                    <GripVertical size={14} style={{ color: 'var(--erp-text-muted)', flexShrink: 0 }} />
                    <span style={{ width: 26, textAlign: 'right', color: 'var(--erp-text-muted)', fontSize: 10, fontFamily: 'var(--erp-font-mono)' }}>{attr.DisplayOrder}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{attr.Label}</span>
                    <span style={{ fontSize: 10, color: 'var(--erp-text-muted)', fontFamily: 'var(--erp-font-mono)', minWidth: 80 }}>{attr.AttributeId}</span>
                    <span style={{ fontSize: 9, background: 'var(--erp-surface-alt)', padding: '1px 5px', borderRadius: 3, border: '1px solid var(--erp-border)' }}>{attr.ControlType}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// TIER 3 — Country Settings (Globalization)
// ═══════════════════════════════════════════════════
const CountrySettingsTab = () => {
  const [countries, setCountries] = useState([]);
  const [region, setRegion] = useState(null);
  const [selected, setSelected] = useState('');
  const [source, setSource] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/attribute-engine/countries`, { headers: hdrs() }).then(r => r.json()).then(j => { if (j.success) setCountries(j.data); }).catch(() => {});
    fetch(`${API_URL}/api/attribute-engine/region`, { headers: hdrs() }).then(r => r.json()).then(j => { if (j.success) { setRegion(j.data); setSelected(j.data?.CountryCode || ''); setSource(j.source); } }).catch(() => {});
  }, []);

  const preview = async (code) => {
    setSelected(code);
    const r = await fetch(`${API_URL}/api/attribute-engine/region/${code}`, { headers: hdrs() });
    const j = await r.json();
    if (j.success) { setRegion(j.data); setSource('preview'); }
  };

  const sample = 1234567.89;
  const fmtNumber = region ? new Intl.NumberFormat(region.NumberLocale || 'en-US', { minimumFractionDigits: region.NumDecimals ?? 2, maximumFractionDigits: region.NumDecimals ?? 2 }).format(sample) : sample;
  const fmtCurrency = region ? `${region.CurrencySymbol || ''}${fmtNumber}` : sample;
  const fmtDate = region ? new Intl.DateTimeFormat(region.NumberLocale || 'en-US').format(new Date()) : '';

  return (
    <div style={{ padding: 'var(--erp-sp-3)' }} data-testid="country-settings-tab">
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 12, color: 'var(--erp-primary)' }}>
        Country & Number Format Settings
      </div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 340px' }}>
          <div className="erp-form-row">
            <label className="erp-form-label">Preview Country <span style={{ fontWeight: 400, color: 'var(--erp-text-muted)', fontSize: 10 }}>({source === 'client-default' ? "your tenant default" : source === 'preview' ? 'previewing' : 'system default'})</span></label>
            <div className="erp-form-control">
              <select className="erp-input erp-select" value={selected} onChange={e => preview(e.target.value)} data-testid="globalization-country-select">
                {countries.map(c => <option key={c.CountryCode} value={c.CountryCode}>{c.FlagEmoji} {c.CountryName}</option>)}
              </select>
            </div>
          </div>
          {region && (
            <div className="erp-form-card" style={{ marginTop: 12 }} data-testid="globalization-preview-card">
              <div style={{ padding: 'var(--erp-sp-2)', fontWeight: 600, fontSize: 12, color: 'var(--erp-primary)' }}>Live Format Preview — {region.CountryName}</div>
              <div style={{ padding: 'var(--erp-sp-2)', fontSize: 12, lineHeight: 2 }}>
                <div>Number: <b data-testid="globalization-preview-number">{fmtNumber}</b></div>
                <div>Currency ({region.CurrencyCode}): <b data-testid="globalization-preview-currency">{fmtCurrency}</b></div>
                <div>Date ({region.DateFormat}): <b data-testid="globalization-preview-date">{fmtDate}</b></div>
                <div>Tax System: <b>{region.TaxSystem} — {region.TaxRatePrimary}% ({region.TaxRegTerm})</b></div>
                <div>Decimal Sep: <b>"{region.DecimalSep}"</b> · Thousands: <b>"{region.ThousandsSep}"</b> · Digits: <b>{region.NumDecimals}</b></div>
              </div>
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', lineHeight: 1.7 }}>
            <p><b>How country settings affect the application:</b></p>
            <ul style={{ paddingLeft: 18, margin: '8px 0' }}>
              <li>Number format (decimal & thousands separators, digits) is applied to all numeric fields in documents.</li>
              <li>Currency symbol and code are shown in pricing fields and reports.</li>
              <li>Date format controls how dates are displayed across all screens.</li>
              <li>Tax system (GST/VAT/Sales Tax) determines which tax fields appear on invoices.</li>
            </ul>
            <p>The active country is read from your tenant's profile in <b>ClientMaster.country_code</b>. Use this panel to preview how formatting looks for any of the 196 supported countries.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════
// SCREEN
// ═══════════════════════════════════════════════════
const AttributeStudioScreen = ({ theme = 'light', onBack, onClose }) => {
  const [tab, setTab] = useState('library');
  const [groups, setGroups] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyTaxSystem, setCompanyTaxSystem] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [g, a, i, reg] = await Promise.all([
        fetch(`${API_URL}/api/attribute-engine/groups`, { headers: hdrs() }).then(r => r.json()),
        fetch(`${API_URL}/api/attribute-engine/attributes`, { headers: hdrs() }).then(r => r.json()),
        fetch(`${API_URL}/api/attribute-engine/industries`, { headers: hdrs() }).then(r => r.json()),
        fetch(`${API_URL}/api/attribute-engine/region`, { headers: hdrs() }).then(r => r.json()).catch(() => ({ success: false })),
      ]);
      if (g.success) setGroups(g.data);
      if (a.success) setAttributes(a.data);
      if (i.success) setIndustries(i.data);
      if (reg.success && reg.data?.TaxSystem) setCompanyTaxSystem(reg.data.TaxSystem);
    } catch { erpToast.error('Failed to load Attribute Studio data'); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="erp-module" data-testid="attribute-studio-screen">
      <div className="erp-screen-header">
        {onClose && <button className="erp-btn erp-btn-ghost" onClick={onClose} data-testid="attribute-studio-back-btn" style={{ marginRight: 8 }}><ArrowLeft size={13} /> Back</button>}
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Attribute & Pricing Studio</span></button>) : (<span className="erp-screen-title">Attribute & Pricing Studio</span>)}
        <div className="erp-screen-actions" style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>
          {attributes.length} attributes · {industries.length} industries
        </div>
      </div>

      <div className="erp-tabs">
        <button className={`erp-tab ${tab === 'library' ? 'active' : ''}`} onClick={() => setTab('library')} data-testid="tab-library"><Library size={13} /> Attribute Library</button>
        <button className={`erp-tab ${tab === 'ordering' ? 'active' : ''}`} onClick={() => setTab('ordering')} data-testid="tab-ordering"><ListOrdered size={13} /> Attribute Order</button>
        <button className={`erp-tab ${tab === 'matrix' ? 'active' : ''}`} onClick={() => setTab('matrix')} data-testid="tab-matrix"><Network size={13} /> Industry Config</button>
        <button className={`erp-tab ${tab === 'country' ? 'active' : ''}`} onClick={() => setTab('country')} data-testid="tab-country"><Globe2 size={13} /> Country Settings</button>
        <button className={`erp-tab ${tab === 'item-screens' ? 'active' : ''}`} onClick={() => setTab('item-screens')} data-testid="tab-item-screens"><LayoutGrid size={13} /> Item Screens</button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading…</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {tab === 'library' ? (
            <LibraryTab attributes={attributes} groups={groups} industries={industries} onRefresh={refresh} taxSystem={companyTaxSystem} />
          ) : tab === 'ordering' ? (
            <OrderingTab attributes={attributes} groups={groups} onRefresh={refresh} />
          ) : tab === 'matrix' ? (
            <MatrixTab attributes={attributes} groups={groups} industries={industries} onRefresh={refresh} />
          ) : tab === 'item-screens' ? (
            <ItemScreensTab industries={industries} taxSystem={companyTaxSystem} />
          ) : (
            <CountrySettingsTab />
          )}
        </div>
      )}
    </div>
  );
};

export default AttributeStudioScreen;
